import { ErrorMessage, Field, Form, Formik } from 'formik';
import { Link, NavigateFunction, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import AuthenticationService from '../../services/AuthenticationService';
import { useState } from 'react';

interface SignInInterface {
  username: string;
  password: string;
}

const SignIn = () => {
  const initialValues: SignInInterface = {
    username: '',
    password: '',
  };
  const validationSchema = Yup.object().shape({
    username: Yup.string().required('Username is required!'),
    password: Yup.string().required('Password is required!'),
  });

  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  let navigate: NavigateFunction = useNavigate();

  const onSubmit = async (
    values: { username: string; password: string },
    { setSubmitting }: any,
  ) => {
    const { username, password } = values;
    setMessage('');
    setLoading(true);

    const response = AuthenticationService.login(username, password).then(
      () => {
        navigate('/');
        window.location.reload();
      },
      (error) => {
        const resMessage =
          (error.response &&
            error.response.data &&
            error.response.data.message) ||
          error.message ||
          error.toString();

        setLoading(false);
        setMessage(resMessage);
      },
    );
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-4 sm:p-6 lg:p-8">
      <div className="w-full max-w-6xl">
        <div className="rounded-2xl shadow-2xl overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            {/* Left Side - Branding */}
            <div className="relative bg-[#2C3E50] p-6 sm:p-12 lg:p-16 flex flex-col justify-center order-1 lg:order-1 min-h-[200px] lg:min-h-[600px]">
              {/* Background Pattern */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute inset-0" style={{
                  backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                  backgroundSize: '40px 40px'
                }}></div>
              </div>

              {/* Content */}
              <div className="relative z-10">
                <Link to="/" className="inline-block mb-4 lg:mb-12">
                  <img
                    src="https://techdenovo-client.s3.ap-south-1.amazonaws.com/motoioi/motoioi-logo.webp"
                    alt="Motoioi Logo"
                    className="h-12 sm:h-16 lg:h-24 w-auto"
                  />
                </Link>

                <h1 className="text-2xl sm:text-3xl lg:text-5xl font-bold text-white mb-2 lg:mb-6 leading-tight">
                  Welcome Back
                </h1>

                {/* Decorative Elements */}
                <div className="mt-4 lg:mt-8 flex items-center space-x-4">
                  <div className="h-1 w-12 bg-primary rounded-full"></div>
                  <div className="h-1 w-8 bg-primary/70 rounded-full"></div>
                  <div className="h-1 w-4 bg-primary/40 rounded-full"></div>
                </div>
              </div>
            </div>

            {/* Right Side - Login Form */}
            <div className="bg-white dark:bg-boxdark p-6 sm:p-12 lg:p-16 flex flex-col justify-center order-2 lg:order-2">
              <div className="w-full max-w-md mx-auto">
                <div className="mb-6 lg:mb-10">
                  <span className="text-xs sm:text-sm font-semibold text-primary uppercase tracking-wide">Admin Portal</span>
                  <h2 className="mt-1 sm:mt-2 text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white">
                    Sign In
                  </h2>
                  <p className="mt-1 sm:mt-2 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                    Enter your credentials to access the dashboard
                  </p>
                </div>

                {/* Error Message */}
                {message && (
                  <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <p className="text-xs sm:text-sm text-red-600 dark:text-red-400">{message}</p>
                  </div>
                )}

                <Formik
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  onSubmit={onSubmit}
                >
                  {({ errors, touched }) => (
                    <Form className="space-y-4 sm:space-y-6">
                      {/* Username Field */}
                      <div>
                        <label htmlFor="username" className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 sm:mb-2">
                          Email Address
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                            <svg
                              className="h-5 w-5 text-gray-400"
                              fill="none"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                            </svg>
                          </div>
                          <Field
                            type="email"
                            name="username"
                            id="username"
                            disabled={loading}
                            placeholder="admin@example.com"
                            className={`w-full pl-12 pr-4 py-3 sm:py-3.5 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm sm:text-base ${
                              errors.username && touched.username
                                ? 'border-red-500'
                                : 'border-gray-300 dark:border-gray-600'
                            } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                          />
                        </div>
                        <ErrorMessage
                          name="username"
                          component="div"
                          className="mt-1.5 text-sm text-red-600 dark:text-red-400"
                        />
                      </div>

                      {/* Password Field */}
                      <div>
                        <label htmlFor="password" className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 sm:mb-2">
                          Password
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                            <svg
                              className="h-5 w-5 text-gray-400"
                              fill="none"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                          </div>
                          <Field
                            type="password"
                            name="password"
                            id="password"
                            disabled={loading}
                            placeholder="••••••••"
                            className={`w-full pl-12 pr-4 py-3 sm:py-3.5 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm sm:text-base ${
                              errors.password && touched.password
                                ? 'border-red-500'
                                : 'border-gray-300 dark:border-gray-600'
                            } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                          />
                        </div>
                        <ErrorMessage
                          name="password"
                          component="div"
                          className="mt-1.5 text-sm text-red-600 dark:text-red-400"
                        />
                      </div>

                      {/* Submit Button */}
                      <div className="pt-2">
                        <button
                          type="submit"
                          disabled={loading}
                          className={`w-full flex items-center justify-center py-3.5 px-4 border border-transparent rounded-lg text-white text-base font-medium transition-all duration-200 ${
                            loading
                              ? 'bg-primary/70 cursor-not-allowed'
                              : 'bg-primary hover:bg-primary/90 hover:shadow-lg transform hover:-translate-y-0.5'
                          } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary`}
                        >
                          {loading ? (
                            <>
                              <svg
                                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                              </svg>
                              Signing in...
                            </>
                          ) : (
                            <>
                              Sign In
                              <svg
                                className="ml-2 -mr-1 h-5 w-5"
                                fill="none"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path d="M13 7l5 5m0 0l-5 5m5-5H6" />
                              </svg>
                            </>
                          )}
                        </button>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
